import React from "react";
import { useMsal } from "@azure/msal-react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { loginRequest } from "../../AuthConfig";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";

enum LoginStatus {
  Startup = "startup", // Initial status before interaction occurs
  AcquireCSRF = "acquireCSRF", // Status set for csrf calls
  Login = "login", // Status set when all login calls occuring
  HandleRedirect = "handleRedirect", // Status set when handleRedirect in progress
  None = "none", // Status set when interaction is complete
}

export function Login() {
  const [loginStatus, setLoginStatus] = React.useState<LoginStatus>(
    LoginStatus.None
  );

  React.useEffect(() => {
    document.title = "Action Appointments";
  }, []);

  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies] = useCookies(["identity"]);
  const loading = inProgress === "login" || loginStatus !== LoginStatus.None;

  const handleLogin = () => {
    setLoginStatus(LoginStatus.Startup);

    instance
      .loginPopup(loginRequest)
      .then((result) => {
        setLoginStatus(LoginStatus.AcquireCSRF);
        return axios.get("/set-csrf/").then(() => result);
      })
      .then((result) => {
        setLoginStatus(LoginStatus.Login);
        return axios.post("/social/azuread-v2-tenant-oauth2/", {
          access_token: result.idToken,
        });
      })
      .then(() => {
        setLoginStatus(LoginStatus.HandleRedirect);
      })
      .catch((error) => {
        console.log(error);
        setLoginStatus(LoginStatus.None);
      });
  };

  React.useEffect(() => {
    if (cookies.identity) {
      const from = (location.state as { from?: Location })?.from?.pathname;
      // Treat "/" (and missing) as "no previous destination" — the root path has
      // no content of its own, so always land on /candidates in that case.
      navigate(from && from !== "/" ? from : "/candidates", { replace: true });
    }
  }, [cookies.identity, navigate, location.state]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Card sx={{ maxWidth: 407 }} variant="outlined">
        {loading ? <LinearProgress /> : null}
        <CardMedia
          component="img"
          alt="action appointments"
          height="351"
          width="395"
          image="/home-tree.webp"
          sx={{ pl: 1.5, pt: 2 }}
        />
        <CardContent sx={{ pb: 0.5 }}>
          <Typography
            gutterBottom
            variant="h5"
            component="div"
            sx={{ mb: "0.2em" }}
          >
            Action Appointments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Development Recruitment Specialists
          </Typography>
        </CardContent>
        <CardActions>
          <Button
            variant="contained"
            fullWidth={true}
            size="large"
            disableElevation
            color="error"
            onClick={handleLogin}
            disabled={loading}
          >
            Sign in
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
}

export default Login;
